import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart } from "lucide-react";

const ProductCard = ({ product, onAddToCart, loading }) => {
  const [quantity, setQuantity] = useState(1);
  const [sizes, setSizes] = useState([]);
  const [hasModel, setHasModel] = useState(false);
  let navigate = useNavigate();
  const [priceRange, setPriceRange] = useState({
    minPrice: product?.price,
    maxPrice: product?.price,
  });

  const isSoldOut = product?.soldOut;

  const handleButtonClick = () => {
    if (isSoldOut) return; // Prevent adding to cart if sold out
    if (hasSizes || hasModel) {
      // Navigate to product details page
      navigate(`/product-details/${product._id}`);
    } else {
      onAddToCart(product._id, quantity);
    }
  };

  useEffect(() => {
    if (product?.category?.name === "Laptop Skin") {
      setHasModel(true);
    }
    if (product?.category?.sizes?.length > 0) {
      const sizesArray = product.category.sizes;
      setSizes(sizesArray);

      // Calculate min and max price based on sizes
      const minPrice = Math.min(...sizesArray.map((size) => size.price));
      const maxPrice = Math.max(...sizesArray.map((size) => size.price));
      setPriceRange({ minPrice, maxPrice });
    } else {
      // Reset to product price if no sizes are available
      setPriceRange({ minPrice: product?.price, maxPrice: product?.price });
    }
  }, [product]);

  const handleQuantityChange = (e) => {
    const value = Math.max(1, Number(e.target.value)); // Ensures minimum quantity of 1
    setQuantity(value);
  };

  const hasSizes = sizes.length > 0;

  return (
    <div className="cursor-pointer bg-white border-2 hover:scale-105 hover:transition-all rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full relative">
      {isSoldOut && (
        <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
          Sold Out
        </div>
      )}{" "}
      <Link to={`/product-details/${product._id}`} className="block">
        <img
          src={product.imageCover}
          alt={product.title}
          className="w-full h-48 object-cover"
        />
      </Link>
      <div className="p-3">
        <Link to={`/product-details/${product._id}`} className="block mb-2">
          <h3 className="text-sm font-semibold text-gray-800 hover:text-red-700 transition-colors line-clamp-1">
            {product.title}
          </h3>
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
            {product?.category?.name}
          </p>
        </Link>
        <div className="flex items-center justify-between mt-2">
          <span className="text-lg font-extrabold text-main">
            {hasSizes
              ? `${priceRange.minPrice} ~ ${priceRange.maxPrice} EGP`
              : `${product?.price} EGP`}
          </span>
          {!hasSizes && !hasModel && (
            <span className="ml-2 text-sm text-gray-500 line-through">
              EGP {(product.price * 1.15).toFixed(2)}
            </span>
          )}
        </div>
        <div className="flex items-center mt-2 gap-2">
          {!hasSizes && !hasModel && !isSoldOut && (
            <input
              type="number"
              value={quantity}
              onChange={handleQuantityChange}
              className="w-14 p-1 text-center text-gray-700 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              min="1"
            />
          )}
          <button
            onClick={handleButtonClick}
            disabled={loading || isSoldOut}
            className="flex-1 explore-btn py-2 rounded-md hover:explore-btn transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-medium"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : isSoldOut ? (
              <p>Sold Out</p> // Display Sold Out button text
            ) : (
              <>
                {hasModel
                  ? "Add Your Model"
                  : hasSizes
                  ? "Choose the size"
                  : "Add to Cart"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
